import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Circle,
  Loader2,
  Clock,
  ExternalLink,
  Mail,
  AlertCircle,
} from "lucide-react";
import { fetchProjectByToken } from "@/lib/api";
import { TRACK } from "@/constants/testIds";

function formatDate(iso) {
  if (!iso) return "TBD";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ProjectTrack() {
  const { token } = useParams();
  const [proj, setProj] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    fetchProjectByToken(token)
      .then((p) => {
        if (!mounted) return;
        setProj(p);
        setStatus("ok");
      })
      .catch((e) => {
        if (!mounted) return;
        setErr(e?.response?.data?.detail || "Project not found");
        setStatus("error");
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6">
        <div className="max-w-md w-full bg-[#111] border border-[#27272A] rounded-3xl p-10 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h1 className="font-display text-2xl font-bold mt-6">
            Tracking link invalid
          </h1>
          <p className="text-zinc-400 mt-2">{err}</p>
          <p className="text-zinc-500 mt-4 text-sm font-mono-jb">
            Please double-check your link or contact us.
          </p>
        </div>
      </div>
    );
  }

  const stages = proj.stages || [];
  const total = stages.length || 1;
  const completed = stages.filter((s) => s.status === "completed").length;
  const inProgress = stages.findIndex((s) => s.status === "in_progress");
  // Progress percent: completed full + half if in_progress
  const percent = Math.min(
    100,
    Math.round(
      ((completed + (inProgress >= 0 ? 0.5 : 0)) / total) * 100
    )
  );
  const isDone = proj.status === "completed" || completed === total;

  return (
    <div
      data-testid={TRACK.page}
      className="min-h-screen bg-[#050505] py-12 px-6"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bento-card p-8 md:p-10 relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#CCFF00]/20 blur-3xl" />

          <div className="relative">
            <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#CCFF00]">
              / Project Tracker
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
              Hey {proj.customer_name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-zinc-400 mt-3 text-lg">
              Here&apos;s a live view of your{" "}
              <span className="text-white font-semibold">
                {proj.package_name}
              </span>{" "}
              build.
            </p>

            {/* Progress bar */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono-jb text-xs uppercase tracking-widest text-zinc-400">
                  Overall Progress
                </span>
                <span className="font-display text-2xl font-black text-[#CCFF00]">
                  {percent}%
                </span>
              </div>
              <div
                data-testid={TRACK.progress}
                className="h-3 rounded-full bg-zinc-800 overflow-hidden"
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percent}%` }}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-[#CCFF00] to-[#D9FF33]"
                  style={{ boxShadow: "0 0 20px rgba(204,255,0,0.55)" }}
                />
              </div>
              {isDone && (
                <p className="mt-3 text-[#CCFF00] font-mono-jb text-xs uppercase tracking-widest">
                  ✓ Project Complete — Congrats!
                </p>
              )}
            </div>

            {/* ETA & Status */}
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              <div className="bg-[#0a0a0a] border border-[#27272A] rounded-2xl p-5">
                <div className="flex items-center gap-2 text-xs font-mono-jb uppercase tracking-widest text-zinc-500">
                  <Clock className="w-3.5 h-3.5" /> Target Launch
                </div>
                <div className="font-display text-2xl font-bold mt-2">
                  {formatDate(proj.eta)}
                </div>
              </div>
              <div className="bg-[#0a0a0a] border border-[#27272A] rounded-2xl p-5">
                <div className="flex items-center gap-2 text-xs font-mono-jb uppercase tracking-widest text-zinc-500">
                  <Circle className="w-3.5 h-3.5" /> Status
                </div>
                <div className="font-display text-2xl font-bold mt-2 capitalize">
                  {isDone
                    ? "Completed"
                    : inProgress >= 0
                    ? stages[inProgress]?.name
                    : "Starting Soon"}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Message from team */}
        {proj.message && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="bento-card p-6 mt-6 border-l-4 border-[#CCFF00]"
          >
            <div className="flex items-center gap-2 text-xs font-mono-jb uppercase tracking-widest text-[#CCFF00] mb-2">
              <Mail className="w-3.5 h-3.5" /> Note from our team
            </div>
            <p className="text-zinc-200 text-lg leading-relaxed">{proj.message}</p>
          </motion.div>
        )}

        {/* Stages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-6"
        >
          <h2 className="font-display text-2xl font-bold mb-4">Milestones</h2>
          <div className="space-y-3">
            {stages.map((s, i) => {
              const isCompleted = s.status === "completed";
              const isCurrent = s.status === "in_progress";
              return (
                <div
                  key={i}
                  data-testid={TRACK.stage(i)}
                  className={`bento-card p-5 flex items-start gap-4 ${
                    isCurrent
                      ? "border-[#CCFF00]/60 shadow-[0_0_30px_rgba(204,255,0,0.12)]"
                      : ""
                  }`}
                >
                  <div className="shrink-0 mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 className="w-7 h-7 text-[#CCFF00]" />
                    ) : isCurrent ? (
                      <div className="w-7 h-7 rounded-full bg-[#CCFF00]/10 border-2 border-[#CCFF00] flex items-center justify-center">
                        <Loader2 className="w-3.5 h-3.5 text-[#CCFF00] animate-spin" />
                      </div>
                    ) : (
                      <Circle className="w-7 h-7 text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-lg font-bold">
                        {s.name}
                      </h3>
                      <span
                        className={`text-[10px] font-mono-jb uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          isCompleted
                            ? "bg-[#CCFF00]/10 text-[#CCFF00]"
                            : isCurrent
                            ? "bg-[#CCFF00]/20 text-[#CCFF00]"
                            : "bg-zinc-800 text-zinc-500"
                        }`}
                      >
                        {isCompleted
                          ? "Done"
                          : isCurrent
                          ? "In Progress"
                          : "Pending"}
                      </span>
                      {s.completed_at && (
                        <span className="text-xs text-zinc-500 font-mono-jb">
                          {formatDate(s.completed_at)}
                        </span>
                      )}
                    </div>
                    {s.note && (
                      <p className="text-zinc-400 mt-2 text-sm">{s.note}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Preview Links */}
        {proj.preview_links && proj.preview_links.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-6"
          >
            <h2 className="font-display text-2xl font-bold mb-4">
              Previews & Files
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {proj.preview_links.map((p, i) => (
                <a
                  key={i}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bento-card p-5 flex items-center justify-between group"
                >
                  <span className="font-medium">{p.label || p.url}</span>
                  <ExternalLink className="w-4 h-4 text-zinc-400 group-hover:text-[#CCFF00]" />
                </a>
              ))}
            </div>
          </motion.div>
        )}

        <p className="text-center text-zinc-500 text-xs font-mono-jb mt-12">
          Updated {formatDate(proj.updated_at)} · Refresh to see latest status
        </p>
      </div>
    </div>
  );
}
